import { useEffect, useState } from 'react';
import * as React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Layout, Menu, MenuProps } from 'antd';
import { useStyles } from '../style';

const { Content, Sider } = Layout

const menuItems: MenuProps['items'] = [
  {
    key: '1',
    label: <Link to="/company-data">Company Data</Link>,
  },
  {
    key: '2',
    label: 'Market Data',
    children: [
      {
        key: '22',
        label: <Link to="/equity-data">Company Share Prices</Link>,
      },
      {
        key: '24',
        label: <Link to="/custom-built-index">Custom-Built Index</Link>,
      },
      {
        key: '23',
        label: <Link to="/traded-funds">Traded Funds</Link>,
      },
      {
        key: '21',
        label: <Link to="/currency-data">Currency Exchange Rates</Link>,
      },
    ]
  },
  {
    key: '3',
    label: 'Capitalization',
    children: [
      {
        key: '31',
        label: <Link to="/actuals?section=summary">Summary Metrics</Link>,
      },
      {
        key: '32',
        label: <Link to="/actuals?section=instrument">Instrument Value</Link>,
      },
      {
        key: '33',
        label: <Link to="/actuals?section=actuals">Actuals</Link>,
      },
    ]
  },
]

interface FrameProps {
  children: React.ReactNode,
}

interface LevelKeysProps {
  key?: string;
  children?: LevelKeysProps[];
}

const getLevelKeys = (items1: LevelKeysProps[]) => {
  const key: Record<string, number> = {};
  const func = (items2: LevelKeysProps[], level = 1) => {
    items2.forEach((item) => {
      if (item.key) {
        key[item.key] = level;
      }
      if (item.children) {
        func(item.children, level + 1);
      }
    });
  };
  func(items1);
  return key;
};

const levelKeys = getLevelKeys(menuItems as LevelKeysProps[]);

const Frame = (props: FrameProps) => {
  const styles = useStyles();
  const navigate = useNavigate();
  const location = useLocation();

  const [stateOpenKeys, setStateOpenKeys] = useState<string[]>(['2']);
  const [selectedKeys, setSelectedKeys] = useState<string[]>(['24']);

  const onOpenChange: MenuProps['onOpenChange'] = (openKeys) => {
    const currentOpenKey = openKeys.find((key) => stateOpenKeys.indexOf(key) === -1);
    // open
    if (currentOpenKey !== undefined) {
      const repeatIndex = openKeys
        .filter((key) => key !== currentOpenKey)
        .findIndex((key) => levelKeys[key] === levelKeys[currentOpenKey]);

      setStateOpenKeys(
        openKeys
          // remove repeat key
          .filter((_, index) => index !== repeatIndex)
          // remove current level all child
          .filter((key) => levelKeys[key] <= levelKeys[currentOpenKey]),
      );
    } else {
      // close
      setStateOpenKeys(openKeys);
    }
  };

  useEffect(() => {
    const path = location.pathname;
    if (path === '/company-data') {
      setSelectedKeys(['1']);
    } else if (path === '/currency-data') {
      setSelectedKeys(['21']);
    } else if (path === '/equity-data') {
      setSelectedKeys(['22']);
    } else if (path === '/traded-funds') {
      setSelectedKeys(['23']);
      setStateOpenKeys(['2']);
    } else if (path === '/custom-built-index') {
      setSelectedKeys(['24']);
      setStateOpenKeys(['2']);
    } else if (path === '/actuals') {
      const params = new URLSearchParams(location.search);
      const section = params.get('section');

      if (section === 'summary') {
        setSelectedKeys(['31']);
      } else if (section === 'instrument') {
        setSelectedKeys(['32']);
      } else {
        setSelectedKeys(['33']);
      }
      setStateOpenKeys(['3']);
    }
  }, [location.pathname]);

  useEffect(() => {
    let redirectTo = localStorage.getItem("redirectTo");
    localStorage.removeItem("redirectTo");
    if (!redirectTo) {
      redirectTo = '/custom-built-index'; // default to actuals
    }
    
    // Normalize redirectTo - remove leading slash for switch comparison
    const normalizedRedirect = redirectTo.startsWith('/') ? redirectTo.slice(1) : redirectTo;
    
    switch (normalizedRedirect) {
      case 'company-data':
        navigate('/company-data');
        onOpenChange([]);
        setSelectedKeys(['1']);
        break;
      case 'currency-data':
        navigate('/currency-data');
        onOpenChange(['2']);
        setSelectedKeys(['21']);
        break;
      case 'equity-data':
        navigate('/equity-data');
        onOpenChange(['2']);
        setSelectedKeys(['22']);
        break;
      case 'traded-funds':
        navigate('/traded-funds');
        onOpenChange(['2']);
        setSelectedKeys(['23']);
        break;
      case 'custom-built-index':
        navigate('/custom-built-index');
        onOpenChange(['2']);
        setSelectedKeys(['24']);
        break;
      case 'actuals':
        navigate('/actuals?section=actuals');
        onOpenChange(['3']);
        setSelectedKeys(['33']);
        break;
      default:
        // If redirectTo already has a leading slash, navigate to it directly
        if (redirectTo.startsWith('/')) {
          navigate(redirectTo);
        }
        onOpenChange([]);
    }
  }, []);

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Layout>
        {/* Sidebar */}
        <Sider
          width={250}
          theme='dark'
          style={{
            position: 'fixed',
            height: '100vh',
            left: 0,
            top: 0,
            bottom: 0,
            overflow: 'auto',
            zIndex: 1000,
            backgroundColor: 'var(--color-as-dark-blue)',
          }}
          className="site-layout-background"
        >
          <div style={{ height: '100%', borderRight: 0 }}>
            <div className={styles.center} style={{ padding: '20px' }}>
              <img src={"assets/AlphaScript-logo.png"} alt="Alpha Script Logo" style={{ width: '70%' }} />
            </div>
            <Menu
              theme='dark'
              mode="inline"
              openKeys={stateOpenKeys}
              onOpenChange={onOpenChange}
              selectedKeys={selectedKeys}
              style={{ borderRight: 0, backgroundColor: 'var(--color-as-dark-blue)' }}
              items={menuItems}
              className={styles.menuStyles}
            />
          </div>
        </Sider>

        {/* Content Area */}
        <Layout style={{ padding: '0 24px 24px', marginLeft: 250, width: 'calc(100%-250px)' }}>
          {/* Main Content */}
          <Content
            className="site-layout-background"
            style={{
              padding: 0,
              margin: 0,
              minHeight: 280,
            }}
          >
            {props.children}
          </Content>
        </Layout>
      </Layout>
    </Layout>
  )
}

export default Frame;
